import { useState, useCallback } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useGetExternalItemDescriptionsQuery,
    useCreateExternalItemDescriptionMutation,
    useUpdateExternalItemDescriptionMutation
} from '../../../../api/endpoints/parts';
import toastService from '../../../../utils/toastService';

const useExternalItemDescription = () => {
    const { th } = useLanguage();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [partCode, setPartCode] = useState('');
    const [partName, setPartName] = useState(''); 

    // API queries
    const { data, isLoading, refetch } = useGetExternalItemDescriptionsQuery({
        pageNumber: page,
        pageSize,
        partCode: partCode || undefined,
        partName: partName || undefined
    });

    const [createItem, { isLoading: isCreating }] = useCreateExternalItemDescriptionMutation();
    const [updateItem, { isLoading: isUpdating }] = useUpdateExternalItemDescriptionMutation();

    const items = data?.items || [];
    const totalRecords = data?.totalCount || 0;

    // Handlers
    const handleNew = useCallback(() => {
        setSelectedItem(null);
        setDialogVisible(true);
    }, []);

    const handleEdit = useCallback((rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    }, []);

    const handleSave = useCallback(async (formData) => {
        try {
            if (formData.id) {
                await updateItem(formData).unwrap();
                toastService.success(th('externalItemDescription.toast.updateSuccess') || 'Kalem başarıyla güncellendi');
            } else {
                await createItem(formData).unwrap();
                toastService.success(th('externalItemDescription.toast.createSuccess') || 'Kalem başarıyla oluşturuldu');
            }
            // invalidatesTags otomatik olarak refetch yapacak, ama yine de manuel refetch ekliyoruz
            await refetch();
            setDialogVisible(false);
            setSelectedItem(null);
        } catch (error) {
            console.error('Save error:', error);
            toastService.error(th('externalItemDescription.toast.saveError') || 'Kayıt sırasında bir hata oluştu');
            throw error;
        }
    }, [createItem, updateItem, refetch, th]);


    const handlePage = useCallback((event) => {
        setPage(event.page + 1);
        setPageSize(event.rows);
    }, []);


    const handleApplyFilters = useCallback(async (filters) => {
    
        setPartCode(filters.partCode || '');
        setPartName(filters.partName || '');
        setPage(1);
        // Filtreleri uyguladıktan sonra getAll endpoint'ini tetikle
        await refetch();
    }, [refetch]);

    const handleResetFilters = useCallback(async () => {
         setPartCode('');
         setPartName('');
        setPage(1);
        // Filtreleri sıfırladıktan sonra getAll endpoint'ini tetikle
        await refetch();
    }, [refetch]);

    const handleCloseDialog = useCallback(() => {
        setDialogVisible(false);
        setSelectedItem(null);
    }, []);

    return {
        // Data
        items,
        totalRecords,
        isLoading,
        isCreating,
        isUpdating,
        
        // Dialog state
        dialogVisible,
        selectedItem,
        
        // Search & pagination    
        page,
        pageSize,
        
        // Handlers
        handleNew,
        handleEdit,
        handleSave,
        handlePage,
        handleCloseDialog,
        handleApplyFilters,
        handleResetFilters
    };
};

export default useExternalItemDescription;

